// PROTOTYPE: This file was scaffolded quickly and is pending a rewrite. Do not use as a model for other parts of the codebase.
import { useCallback, useState } from 'react'
import { Search } from 'lucide-react'
import type { Persona } from '@storefronts/common-types'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogCloseButton,
    DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { usePersonasQuery } from '@/hooks/personaApiHooks'
import { cn } from '@/lib/utils'

function personaInitials(name: string): string {
    return name
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? '')
        .join('')
}

const AVATAR_COLORS = [
    'bg-blue-100 text-blue-700',
    'bg-green-100 text-green-700',
    'bg-purple-100 text-purple-700',
    'bg-orange-100 text-orange-700',
    'bg-pink-100 text-pink-700',
    'bg-teal-100 text-teal-700',
]

function avatarColor(id: number): string {
    return AVATAR_COLORS[id % AVATAR_COLORS.length]
}

interface PersonaPickerModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSelected: (persona: Persona) => void
    currentPersonaId?: number
}

export function PersonaPickerModal({
    open,
    onOpenChange,
    onSelected,
    currentPersonaId,
}: PersonaPickerModalProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {open && (
                <PersonaPickerContent
                    onOpenChange={onOpenChange}
                    onSelected={onSelected}
                    currentPersonaId={currentPersonaId}
                />
            )}
        </Dialog>
    )
}

function PersonaPickerContent({
    onOpenChange,
    onSelected,
    currentPersonaId,
}: Omit<PersonaPickerModalProps, 'open'>) {
    const { data: personas = [], isLoading } = usePersonasQuery()
    const [search, setSearch] = useState('')
    const [selectedId, setSelectedId] = useState<number | undefined>(
        currentPersonaId,
    )

    const query = search.trim().toLowerCase()
    const filtered = query
        ? personas.filter((p) => p.name.toLowerCase().includes(query))
        : personas

    const handleConfirm = useCallback(() => {
        const persona = personas.find((p) => p.id === selectedId)
        if (!persona) return
        onSelected(persona)
        onOpenChange(false)
    }, [personas, selectedId, onSelected, onOpenChange])

    return (
        <DialogContent className="flex max-h-[70vh] max-w-xl flex-col overflow-hidden">
            <DialogHeader>
                <DialogCloseButton />
                <DialogTitle>Pick a persona</DialogTitle>
                <DialogDescription>
                    Pre-select a persona for the storefront.
                </DialogDescription>
            </DialogHeader>

            <div className="relative">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    placeholder="Search personas..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                        Loading personas...
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                        {search
                            ? 'No personas match your search'
                            : 'No personas found'}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-2 py-2">
                        {filtered.map((persona) => (
                            <button
                                key={persona.id}
                                type="button"
                                className={cn(
                                    'flex items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                                    selectedId === persona.id
                                        ? 'border-foreground/40 bg-muted'
                                        : 'border-border hover:bg-muted/50',
                                )}
                                onClick={() => setSelectedId(persona.id)}
                            >
                                <span
                                    className={cn(
                                        'flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                                        avatarColor(persona.id),
                                    )}
                                >
                                    {personaInitials(persona.name)}
                                </span>
                                <div className="min-w-0">
                                    <div className="truncate text-sm font-medium">
                                        {persona.name}
                                    </div>
                                    {persona.ageRange && (
                                        <div className="truncate text-xs text-muted-foreground">
                                            {persona.ageRange}
                                            {persona.location
                                                ? ` · ${persona.location}`
                                                : ''}
                                        </div>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <DialogFooter className="border-t pt-4 sm:justify-between">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                </Button>
                <Button disabled={!selectedId} onClick={handleConfirm}>
                    Select persona
                </Button>
            </DialogFooter>
        </DialogContent>
    )
}
